import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  getUserGoals,
  getCurrentQuarter,
  getAvailableQuartersForCreation,
  isEmployeeFinalReviewUnlocked,
  getFinalReviewUnlockDate,
  isSelfReviewOpenByDate,
  isGlobalSelfReviewOpen
} from '../../services/goalsService';
import { getDemoGoals } from '../../services/demoData';
import { GoalDocument } from '../../types';
import {
  Plus,
  Clock,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  ClipboardCheck,
  AlertCircle,
  Edit,
  FlaskConical,
  RotateCcw
} from 'lucide-react';
import { generateGoalsDocx } from '../../services/docxService';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-700',
  initial_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-cps-primary/10 text-cps-dark',
  employee_final_review: 'bg-orange-100 text-orange-700',
  final_review: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Awaiting Manager Review',
  initial_review: 'Under Review',
  approved: 'Approved',
  employee_final_review: 'Self-Review in Progress',
  final_review: 'Awaiting Manager Final Review',
  completed: 'Completed',
};

const EmployeeDashboard = () => {
  const { firebaseUser, isDeveloper } = useAuth();
  const { isDemoMode, demoVersion, resetDemo } = useDemo();
  const navigate = useNavigate();
  const [allGoals, setAllGoals] = useState<GoalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    actionRequired: true,
    inProgress: true,
    completed: false,
  });
  const [availableQuarters, setAvailableQuarters] = useState<{ quarter: number; year: number }[]>([]);
  const [globalSelfReviewOpen, setGlobalSelfReviewOpen] = useState<Record<string, boolean>>({});
  const { quarter, year } = getCurrentQuarter();

  useEffect(() => {
    const fetchGoals = async () => {
      if (!firebaseUser && !isDemoMode) return;

      setLoading(true);
      try {
        let goals: GoalDocument[];

        if (isDemoMode) {
          // Demo mode - use demo data, show all as "employee view"
          goals = getDemoGoals();
          // Simulate global self-review being open in demo mode
          const uniqueQuarters = [...new Set(goals.map(g => `${g.quarter}-${g.year}`))];
          const globalChecks: Record<string, boolean> = {};
          for (const qy of uniqueQuarters) {
            globalChecks[qy] = true; // Self-review always open in demo
          }
          setGlobalSelfReviewOpen(globalChecks);
          setAvailableQuarters([{ quarter, year }]); // Allow creating new demo goals
        } else {
          goals = await getUserGoals(firebaseUser!.uid);

          // Check global self-review settings for each unique quarter/year
          const uniqueQuarters = [...new Set(goals.map(g => `${g.quarter}-${g.year}`))];
          const globalChecks: Record<string, boolean> = {};
          for (const qy of uniqueQuarters) {
            const [q, y] = qy.split('-').map(Number);
            globalChecks[qy] = await isGlobalSelfReviewOpen(q, y);
          }
          setGlobalSelfReviewOpen(globalChecks);

          // Calculate available quarters for goal creation
          const allAvailable = getAvailableQuartersForCreation();
          const existingQuarters = new Set(goals.map(g => `${g.quarter}-${g.year}`));
          const canCreate = allAvailable.filter(q => !existingQuarters.has(`${q.quarter}-${q.year}`));
          setAvailableQuarters(canCreate);
        }

        setAllGoals(goals);
      } catch (error) {
        console.error('Error fetching goals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [firebaseUser, isDemoMode, demoVersion, quarter, year]);

  const handleDownload = async (goal: GoalDocument) => {
    try {
      await generateGoalsDocx(goal);
    } catch (error) {
      console.error('Error generating DOCX:', error);
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Group goals by quarter
  const groupByQuarter = (goals: GoalDocument[]) => {
    const grouped: Record<string, GoalDocument[]> = {};
    goals.forEach(goal => {
      const key = `Q${goal.quarter} ${goal.year}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(goal);
    });
    // Sort by year desc, then quarter desc
    return Object.entries(grouped).sort((a, b) => {
      const [qA, yA] = a[0].replace('Q', '').split(' ');
      const [qB, yB] = b[0].replace('Q', '').split(' ');
      if (yB !== yA) return parseInt(yB) - parseInt(yA);
      return parseInt(qB) - parseInt(qA);
    });
  };

  // Goals requiring action from the employee
  // - draft: needs to be completed and submitted
  // - approved/submitted (past quarters): needs self-review
  // - approved/submitted (current quarter): needs self-review IF global/override is open
  // - allowEdit: manager allowed edits
  const actionRequiredGoals = allGoals.filter(g => {
    if (g.status === 'draft' || g.allowEdit) return true;

    if (g.status === 'approved' || g.status === 'submitted') {
      const isPastQuarter = !(g.quarter === quarter && g.year === year);
      const globalOpen = globalSelfReviewOpen[`${g.quarter}-${g.year}`] || false;
      const overrideOpen = isSelfReviewOpenByDate(g);
      const isCurrentQuarterButOpen = (g.quarter === quarter && g.year === year) && (globalOpen || overrideOpen);
      return isPastQuarter || isCurrentQuarterButOpen;
    }

    return false;
  });

  // Goals in progress (waiting on manager or quarter to end)
  // - submitted, initial_review: waiting for manager initial review
  // - approved (current quarter): approved, waiting for quarter to end
  // - final_review: waiting for manager final review
  const inProgressGoals = allGoals.filter(g =>
    g.status === 'submitted' ||
    g.status === 'initial_review' ||
    g.status === 'final_review' ||
    (g.status === 'approved' && g.quarter === quarter && g.year === year)
  );

  // Completed goals
  const completedGoals = allGoals.filter(g => g.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cps-primary"></div>
      </div>
    );
  }

  const renderGoalCard = (goal: GoalDocument) => {
    const isDemo = goal.id?.startsWith('demo-goal-');
    const dateUnlocked = isEmployeeFinalReviewUnlocked(goal.quarter, goal.year, isDeveloper) || isDemoMode;
    const overrideOpen = isSelfReviewOpenByDate(goal);
    const globalOpen = globalSelfReviewOpen[`${goal.quarter}-${goal.year}`] || false;
    const isUnlocked = dateUnlocked || overrideOpen || globalOpen;
    const unlockDate = getFinalReviewUnlockDate(goal.quarter, goal.year);

    // Allow self-review for:
    // 1. 'approved' or 'submitted' goals from PAST quarters, OR
    // 2. 'approved' or 'submitted' goals from CURRENT quarter IF global/override is open
    const isPastQuarter = !(goal.quarter === quarter && goal.year === year);
    const isCurrentQuarterButOpen = (goal.quarter === quarter && goal.year === year) && (globalOpen || overrideOpen);
    const needsSelfReview = (goal.status === 'approved' || goal.status === 'submitted') &&
      (isPastQuarter || isCurrentQuarterButOpen);

    const isDraft = goal.status === 'draft';
    const canEdit = isDraft || goal.allowEdit;

    return (
      <div key={goal.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {isDemo && (
                <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                  <FlaskConical className="w-3 h-3 inline mr-1" />
                  Demo
                </span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[goal.status]}`}>
                {statusLabels[goal.status]}
              </span>
              {goal.allowEdit && !isDraft && (
                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                  Edit Allowed
                </span>
              )}
            </div>
            <p className="font-medium text-gray-900 mb-1">{goal.userName}</p>
            <div className="space-y-1">
              {goal.goals.slice(0, 2).map((g, idx) => (
                <p key={idx} className="text-sm text-gray-600 truncate">
                  {idx + 1}. {g.title || 'Untitled goal'}
                </p>
              ))}
              {goal.goals.length > 2 && (
                <p className="text-xs text-gray-400">+{goal.goals.length - 2} more goals</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(goal);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
              title="Download DOCX"
            >
              <Download className="w-4 h-4" />
            </button>

            {canEdit && (
              <button
                onClick={() => navigate(`/goals/${goal.id}/edit`)}
                className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isDraft
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <Edit className="w-4 h-4" />
                {isDraft ? 'Continue' : 'Edit'}
              </button>
            )}

            {needsSelfReview && isUnlocked && (
              <button
                onClick={() => navigate(`/goals/${goal.id}/self-review`)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition-colors"
              >
                <ClipboardCheck className="w-4 h-4" />
                Self-Review
              </button>
            )}

            {needsSelfReview && !isUnlocked && (
              <div className="flex items-center gap-1 text-orange-600 text-xs">
                <Clock className="w-3 h-3" />
                <span>{unlockDate}</span>
              </div>
            )}

            {!canEdit && !needsSelfReview && (
              <button
                onClick={() => navigate(`/goals/${goal.id}`)}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
              >
                View
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Demo Mode Info */}
      {isDemoMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FlaskConical className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Employee View (Demo Mode)</p>
              <p className="text-sm text-amber-600">Showing sample goals for training purposes</p>
            </div>
          </div>
          <button
            onClick={resetDemo}
            className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Demo Data
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Goals</h1>
          <p className="text-gray-500">Q{quarter} {year}</p>
        </div>
        {availableQuarters.length > 0 && (
          <button
            onClick={() => navigate('/goals/create')}
            className="inline-flex items-center gap-2 bg-cps-primary text-white px-4 py-2 rounded-lg hover:bg-cps-light transition-colors"
          >
            <Plus className="w-5 h-5" />
            {availableQuarters.length === 1
              ? `Create Q${availableQuarters[0].quarter} ${availableQuarters[0].year} Goals`
              : 'Create Goals'
            }
          </button>
        )}
      </div>

      {/* Action Required Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('actionRequired')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-semibold text-gray-900">Action Required</h2>
            <span className="text-sm text-gray-500">({actionRequiredGoals.length})</span>
          </div>
          {expandedSections.actionRequired ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSections.actionRequired && (
          <div className="border-t p-4 space-y-4">
            {actionRequiredGoals.length > 0 ? (
              groupByQuarter(actionRequiredGoals).map(([quarterLabel, goals]) => (
                <div key={quarterLabel}>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                    <span className="text-gray-400">({goals.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {goals.map(goal => renderGoalCard(goal))}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-2" />
                <p className="text-gray-500">No action required - you're all caught up!</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* In Progress Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('inProgress')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-900">In Progress</h2>
            <span className="text-sm text-gray-500">({inProgressGoals.length})</span>
          </div>
          {expandedSections.inProgress ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSections.inProgress && (
          <div className="border-t p-4 space-y-4">
            {inProgressGoals.length > 0 ? (
              groupByQuarter(inProgressGoals).map(([quarterLabel, goals]) => (
                <div key={quarterLabel}>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                    <span className="text-gray-400">({goals.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {goals.map(goal => renderGoalCard(goal))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No goals in progress</p>
            )}
          </div>
        )}
      </div>

      {/* Completed Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          onClick={() => toggleSection('completed')}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            <h2 className="text-lg font-semibold text-gray-900">Completed</h2>
            <span className="text-sm text-gray-500">({completedGoals.length})</span>
          </div>
          {expandedSections.completed ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSections.completed && (
          <div className="border-t p-4 space-y-4">
            {completedGoals.length > 0 ? (
              groupByQuarter(completedGoals).map(([quarterLabel, goals]) => (
                <div key={quarterLabel}>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                    <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">{quarterLabel}</span>
                    <span className="text-gray-400">({goals.length})</span>
                  </h3>
                  <div className="space-y-3">
                    {goals.map(goal => renderGoalCard(goal))}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">No completed goals yet</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;